
import { useCallback } from 'react';
import { toast } from 'sonner';
import { simpleCrawlerService } from '@/services/simpleCrawlerService';
import { enhanceCrawlResult } from '@/utils/crawlerUtils';
import { logger, getSitemapCrawlStats } from '@/utils/logger';

interface CrawlerOperationsProps {
  setIsAnalyzing: (value: boolean) => void;
  setCrawlResult: (result: any) => void;
  setWebsiteUrl: (url: string) => void;
  setSitemapStats: (stats: any) => void;
  handleProgressUpdate: (progressData: any) => void;
}

export function useCrawlerOperations({
  setIsAnalyzing,
  setCrawlResult,
  setWebsiteUrl,
  setSitemapStats,
  handleProgressUpdate
}: CrawlerOperationsProps) {

  const handleSubmit = useCallback(async (url: string) => {
    try {
      setIsAnalyzing(true);
      setWebsiteUrl(url);
      setSitemapStats(null);
      setCrawlResult(null); // Clear previous crawl results
      
      // Enable debug logging for this crawl
      logger.enableDebugMode();
      logger.info(`Starting crawl for ${url}`, { component: 'useCrawler', tags: ['crawl', 'start'] });
      
      // Register progress callback
      simpleCrawlerService.registerProgressCallback(handleProgressUpdate);
      
      // Start crawling
      const result = await simpleCrawlerService.crawlWebsite(url);
      
      // Process result and update state
      const enhancedResult = enhanceCrawlResult(result);
      
      // Ensure we're not adding any properties that might trigger "Top Performing Pages"
      // Using type assertion to access potentially undefined properties
      const resultObj = enhancedResult as any;
      if (resultObj.topPages) {
        delete resultObj.topPages;
      }
      if (resultObj.performancePages) {
        delete resultObj.performancePages;
      }
      if (resultObj.topPerformingPages) {
        delete resultObj.topPerformingPages;
      }
      
      setCrawlResult(enhancedResult);
      
      // Use the sitemap stats from the crawler service
      if (enhancedResult.sitemapStats) {
        setSitemapStats(enhancedResult.sitemapStats);
        
        logger.info(`Crawl completed for ${url}`, { 
          component: 'useCrawler', 
          tags: ['crawl', 'complete'],
          context: { 
            pagesProcessed: result.pagesProcessed,
            duration: result.duration,
            totalUrls: result.totalUrls,
            sitemapStats: enhancedResult.sitemapStats
          }
        });
        
        // Log simple statistics
        console.info(`
================ SITEMAP STATISTICS ================
Total URLs found: ${enhancedResult.sitemapStats.total}
Ready for analysis: ${enhancedResult.sitemapStats.successful}
Load time: ${enhancedResult.sitemapStats.crawlTime.toFixed(2)} seconds
====================================================
`);
        
        // Show a more specific toast message with sitemap stats
        toast.success(`Sitemap loaded successfully! Found ${enhancedResult.sitemapStats.total} URLs ready for analysis.`);
      } else {
        toast.success('Sitemap loaded successfully!');
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      logger.error('Crawl error:', err, { 
        component: 'useCrawler', 
        tags: ['crawl', 'error'],
        context: { url }
      });
      toast.error('Failed to load sitemap. Please try again.');
    } finally {
      setIsAnalyzing(false);
      // Disable debug mode after crawl is complete
      logger.disableDebugMode();
    }
  }, [handleProgressUpdate, setCrawlResult, setIsAnalyzing, setSitemapStats, setWebsiteUrl]);

  const handleCancel = useCallback(() => {
    if (simpleCrawlerService) {
      simpleCrawlerService.cancelCrawl();
      setIsAnalyzing(false);
      toast.info('Sitemap loading cancelled');
    }
  }, [setIsAnalyzing]);

  return {
    handleSubmit,
    handleCancel
  };
}
